import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { analyticsService } from '../lib/analytics/analyticsService';
import { InfoIcon, ItineraryIcon, BookIcon } from './icons';

interface HotelModalProps {
  config: any;
  translations: Record<string, Record<string, string>>;
  onClose: () => void;
}

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

export const HotelModal: React.FC<HotelModalProps> = ({
  config,
  translations,
  onClose
}) => {
  const { i18n } = useTranslation();

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      analyticsService.trackEvent({
        event_type: 'interaction',
        event_name: 'hotel_info_close',
        event_data: {
          close_method: 'overlay'
        }
      });
      onClose();
    }
  }, [onClose]);

  const handleCloseClick = useCallback(() => {
    analyticsService.trackEvent({
      event_type: 'interaction',
      event_name: 'hotel_info_close',
      event_data: {
        close_method: 'button'
      }
    });
    onClose();
  }, [onClose]);

  const handleButtonClick = useCallback((
    action: string,
    externalLink?: string
  ) => {
    analyticsService.trackEvent({
      event_type: 'interaction',
      event_name: `hotel_${action}_click`,
      event_data: {
        hotel_name: config.name,
        url: externalLink
      }
    });

    if (externalLink) {
      window.open(externalLink, '_blank', 'noopener,noreferrer');
    }
  }, [config.name]);

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[400] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
      onMouseDown={stopPropagation}
    >
      <div 
        className="bg-white w-full max-w-lg relative flex flex-col overflow-hidden"
        style={{ borderRadius: config.border_radius }}
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
      >
        {/* Close button */}
        <button
          onClick={handleCloseClick}
          className="absolute top-0 right-0 w-12 h-12 bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-opacity flex items-center justify-center"
          style={{ 
            borderTopRightRadius: config.border_radius,
            borderBottomLeftRadius: config.border_radius
          }}
        >
          <X size={20} />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Hotel name */}
          <h2 className="text-2xl font-bold mb-4">{config.name}</h2>

          {/* Image with link */}
          {config.url && config.image_infobulle && (
            <a 
              href={config.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleButtonClick('website', config.url)}
              className="block w-full aspect-video mb-4 overflow-hidden rounded-lg hover:opacity-90 transition-opacity"
            >
              <img
                src={config.image_infobulle}
                alt={config.name}
                className="w-full h-full object-cover"
              />
            </a>
          )}

          {/* Hotel info */}
          <div className="mb-6">
            {config.fulladress && (
              <p className="text-gray-600 mb-2">
                {config.fulladress}
              </p>
            )}
            {config.telephone && (
              <p className="text-gray-600">
                {config.telephone}
              </p>
            )}
          </div>

          {/* Action buttons - 2 columns grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* More info button */}
            {config.url && (
              <a
                href={config.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleButtonClick('website', config.url)}
                className="flex items-center justify-center px-4 py-2 rounded-lg text-white gap-2"
                style={{ 
                  backgroundColor: config['major-color'],
                  borderRadius: config.border_radius 
                }}
              >
                <InfoIcon size={20} color="white" />
                {getTranslatedText('btn_more_info')}
              </a>
            )}

            {/* Call button */}
            {config.telephone && (
              <a
                href={`tel:${config.telephone}`}
                onClick={() => handleButtonClick('call')}
                className="flex items-center justify-center px-4 py-2 rounded-lg text-white gap-2"
                style={{ 
                  backgroundColor: config['major-color'],
                  borderRadius: config.border_radius 
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                {getTranslatedText('btn_call')}
              </a>
            )}

            {/* Itinerary button */}
            {config.fulladress && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(config.fulladress)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleButtonClick('directions', `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(config.fulladress)}`)}
                className="flex items-center justify-center px-4 py-2 rounded-lg text-white gap-2"
                style={{ 
                  backgroundColor: config['major-color'],
                  borderRadius: config.border_radius 
                }}
              >
                <ItineraryIcon size={20} color="white" />
                {getTranslatedText('btn_itinerary')}
              </a>
            )}

            {/* Booking button */}
            {config.booking && (
              <a
                href={config.booking}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleButtonClick('booking', config.booking)}
                className="flex items-center justify-center px-4 py-2 rounded-lg text-white gap-2"
                style={{ 
                  backgroundColor: config['major-color'],
                  borderRadius: config.border_radius 
                }}
              >
                <BookIcon size={20} color="white" />
                {getTranslatedText('btn_booking')}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};